import * as EmailValidator from 'email-validator';
import EnvironmentVariables from '../../shared/environmentVariables';
import ExternalLinks from '../../shared/externalLinks';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import PageContent from '../page/PageContent';
import ReCAPTCHA from "react-google-recaptcha";
import React, { FunctionComponent, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Alert, Box, Button, Container, TextField, Typography, CircularProgress, Checkbox, FormControl, Select, MenuItem, InputLabel, FormLabel } from '@mui/material';
import { EcommerceEvent } from '../../shared/types';
import { getStyles } from './css/MobileRegistrationFormStyles';
import { useNavigate } from 'react-router-dom';

interface Props {
    countries: { countryCode: string, countryName: string }[]
}

const MobileRegistrationForm: FunctionComponent<Props> = ({ countries }) => {
    const isDesktopOrLaptop = useMediaQuery('(min-width:1224px)');
    const summaryAlignment = isDesktopOrLaptop ? 'unset' : 'center';
    const styles = getStyles(isDesktopOrLaptop);
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [company, setCompany] = useState<string>('');
    const [country, setCountry] = useState<string>('US');
    const [password, setPassword] = useState<string>('');
    const [isSubmitting, setSubmitting] = useState<boolean>(false);
    const [recaptchaValue, setRecaptchaValue] = useState<string | null>();

    const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
    const [termsError, setTermsError] = useState<boolean>(false);
    const [accountExistsError, setAccountExistsError] = useState<boolean>(false);
    const [address, setAddress] = useState<any>(null);
    const [addressError, setAddressError] = useState<boolean>(false);
    const [signUpError, setSignUpError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<boolean>(false);
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [firstNameError, setFirstNameError] = useState<boolean>(false);
    const [lastNameError, setLastNameError] = useState<boolean>(false);
    const [recaptchaError, setRecaptchaError] = useState<boolean>(false);
    const [addressInputBorder, setAddressInputBorder] = useState<string>('1px inset rgba(0, 0, 0, 0.25)');
    const zohoObject = { widgetcode: "be0683af84337cd2b98234a8e336ab3545b9c26aab7929c1fcd46b7a042bdc1b354bae78457b14f0239c5db2c7f58e3a6970830d0bca98e608f2ec39ec1bc0e0", values: {}, ready: function () { } };

    const handleFormSubmit: React.MouseEventHandler<HTMLElement> = async e => {
        e.preventDefault();
        setSubmitting(true);
        window.scrollTo(0, 0);
        setSignUpError(null);

        if (!recaptchaValue) {
            setRecaptchaError(true);
            setSubmitting(false);
            return;
        } else {
            setRecaptchaError(false);
        }

        if (!firstName || !firstName.trim()) {
            setFirstNameError(true);
            setSubmitting(false);
            return;
        } else {
            setFirstNameError(false);
        }

        if (!lastName || !lastName.trim()) {
            setLastNameError(true);
            setSubmitting(false);
            return;
        } else {
            setLastNameError(false);
        }

        if (!password || password.length < 5) {
            setPasswordError(true);
            setSubmitting(false);
            return;
        } else {
            setPasswordError(false);
        }

        if (!agreedToTerms) {
            setTermsError(true);
            setSubmitting(false);
            return;
        } else {
            setTermsError(false);
        }

        if (!address) {
            setAddressError(true);
            setSubmitting(false);
            return;
        } else {
            setAddressError(false);
        }

        if (!EmailValidator.validate(email)) {
            setEmailError(true);
            setSubmitting(false);
            return;
        } else {
            setEmailError(false);
        }

        setAccountExistsError(false);

        try {
            const response = await fetch('/api/mobile-registration', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email,
                    password,
                    firstName,
                    lastName,
                    company,
                    country: country === "US" ? "" : country,
                    address: address.value.description,
                    recaptchaValue
                })
            });

            const data = await response.json();
            if (response.ok) {
                // Building the tracking payload must never block the redirect to the
                // confirmation page - sign-up has already succeeded by this point.
                let ecommerce: EcommerceEvent | undefined;
                try {
                    ecommerce = {
                        currency: "USD",
                        transaction_id: data.transactionId,
                        value: 0,
                        items: [{ item_id: "0", item_name: "VCS Credits", price: 0, quantity: 30 }],
                        credit_card_entered: false
                    };
                } catch (error) {
                    console.error(error);
                }

                if (data.loginToken) {
                    navigate('/subscription/signup-complete', { state: { token: data.loginToken, ecommerce } });
                } else {
                    navigate('/subscription/signup-complete', { state: { ecommerce } });
                }
            }
            else {
                if (response.status === 422) {
                    setAccountExistsError(true);
                }
                else {
                    if (data && data.message) {
                        setSignUpError(data.message);
                    }
                    else {
                        setSignUpError('An error has occurred during sign up. Please try again later.');
                    }
                }
                setSubmitting(false);
                return;
            }
        }
        catch (error) {
            setSignUpError("An error has occurred during sign up. Please try again later.");
            console.log(error);
        }
    }

    const onSetAgreedToTerms = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAgreedToTerms(e.target.checked);
    }

    const onCountryChanged = (e: any) => {
        setCountry(e.target.value)
        setAddress(null);
    }

    return (<Box>
        {isSubmitting && <Box sx={{ backgroundColor: 'white', zIndex: 999 }} position='sticky' top={0} height='100vh'>
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box display="flex" justifyContent="center"><CircularProgress /></Box>
                <Box display="flex" justifyContent="center" marginTop={1}><Typography>Processing registration. Please wait. Do not refresh your browser.</Typography></Box>
            </Box>
        </Box>}
        <PageContent color='tertiary'>
            <Box component='div' m={3}>
                <Container>
                    <Typography color="textPrimary" textAlign={summaryAlignment}>You can add up to 5 properties under this account at no cost. It will provide access to the most advanced AI solution available for lodging providers. There is no expiration date. You can see our <a target="_blank" rel="noopener" href={ExternalLinks.MARKETING.TERMS}>terms of use</a> for more information. In order to fund the development of even more powerful capabilities, some additional features are available by upgrading to a Pro subscription. We appreciate your support!</Typography>
                </Container>
            </Box>
            {accountExistsError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">There is already an existing registered user with that email address. Click <a href={ExternalLinks.VCS_PORTAL.SUBSCRIPTION}>here</a> to log in to an existing account, where you can add credits.</Alert></Box></Box>}
            {addressError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide an address.</Alert></Box></Box>}
            {signUpError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">{signUpError}</Alert></Box></Box>}
            {emailError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide a valid email.</Alert></Box></Box>}
            {passwordError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide a password that's at least 5 characters long.</Alert></Box></Box>}
            {firstNameError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide a first name.</Alert></Box></Box>}
            {lastNameError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please provide a last name.</Alert></Box></Box>}
            {termsError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please accept the terms of use.</Alert></Box></Box>}
            {recaptchaError && <Box display="flex" justifyContent="center"><Box m={2}><Alert severity="warning">Please verify that you are not a robot.</Alert></Box></Box>}
            <Box sx={styles.contentBody}>
                <Box sx={styles.form}>
                    <TextField sx={styles.formItem} required id="email" label="Email" onChange={e => { setEmail(e.target.value) }} />
                    <TextField sx={styles.formItem} required id="first-name" label="First Name" onChange={e => { setFirstName(e.target.value) }} />
                    <TextField sx={styles.formItem} required id="last-name" label="Last Name" onChange={e => { setLastName(e.target.value) }} />
                    <TextField sx={styles.formItem} id="company" label="Company Name (optional)" onChange={e => { setCompany(e.target.value) }} />
                    <TextField sx={styles.formItem} required inputProps={{ minLength: 5 }} id="password" label="Password" type="password" helperText="The password must be at least 5 characters" onChange={e => { setPassword(e.target.value) }} />
                    <FormControl required>
                        <InputLabel>Country</InputLabel>
                        <Select
                            label="Country"
                            id="country-select"
                            placeholder='Country *'
                            value={country}
                            onChange={onCountryChanged}
                        >
                            {countries.map(c => <MenuItem key={c.countryCode} value={c.countryCode ? c.countryCode : "US"}>{c.countryName}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl sx={styles.addressInput} required>
                        <Box sx={styles.addressAutocomplete}>
                            <fieldset
                                style={{
                                    borderRadius: '4px',
                                    border: addressInputBorder,
                                    cursor: 'text'
                                }}
                                onMouseEnter={() => setAddressInputBorder('1px inset rgba(0, 0, 0, 0.87)')}
                                onMouseLeave={() => setAddressInputBorder('1px inset rgba(0, 0, 0, 0.25)')}
                            >
                                {address && <legend style={{ fontSize: "0.75rem", color: 'rgba(0, 0, 0, 0.55)' }}>Address * </legend>}
                                <GooglePlacesAutocomplete apiKey={EnvironmentVariables.GOOGLE_API_KEY} selectProps={{
                                    placeholder: 'Address *',
                                    styles: {
                                        input: (provided: any) => ({
                                            ...provided,
                                            font: 'inherit'
                                        }),
                                        control: (provided: any) => ({
                                            ...provided,
                                            backgroundColor: 'transparent',
                                            boxShadow: 0,
                                            border: 'none',
                                            "&:hover": {
                                                border: "none",
                                                cursor: "pointer",
                                                '&:active': {
                                                    border: 'none'
                                                }
                                            }
                                        }),
                                        menu: (provided: any) => ({
                                            ...provided,
                                            left: '-14px',
                                            width: 'calc(100% + 28px)'
                                        }),
                                        indicatorSeparator: _ => ({
                                            display: 'none'
                                        }),
                                        option: (provided: any) => ({
                                            ...provided,
                                            font: 'inherit',
                                        }),
                                        singleValue: (provided: any) => ({
                                            ...provided,
                                            font: 'inherit'
                                        }),
                                        dropdownIndicator: (provided: any) => ({
                                            ...provided,
                                            display: 'none'
                                        }),
                                        valueContainer: (provided: any) => ({
                                            ...provided,
                                            padding: 0
                                        })
                                    },
                                    value: address,
                                    onChange: setAddress,
                                    isDisabled: !Boolean(country)
                                }}
                                    autocompletionRequest={{ componentRestrictions: { country: [country] }, types: ['address'] }}
                                />
                            </fieldset>
                        </Box>
                    </FormControl>
                    <Box sx={{ my: 2 }}>
                        <ReCAPTCHA sitekey={EnvironmentVariables.RECAPTCHA_SITE_KEY as string} onChange={token => { setRecaptchaValue(token) }} />
                    </Box>
                </Box>
            </Box>
            <FormControl style={{ width: "100%" }} error={termsError}>
                <Box display="flex" justifyContent={'center'} marginTop={1}>
                    <Checkbox onChange={onSetAgreedToTerms} /><Typography style={{ marginTop: "8px" }}>I agree with the <a target="_blank" rel="noopener" href={ExternalLinks.MARKETING.TERMS}>Terms of use</a></Typography>
                </Box>
            </FormControl>
            <Box m={2} display="flex" justifyContent="center" marginBottom={10}>
                <Button disabled={isSubmitting} color="secondary" variant="contained" onClick={handleFormSubmit}>Sign up</Button>
            </Box>
            <script type="text/javascript" id="zsiqchat">var $zoho=$zoho || { };$zoho.salesiq = $zoho.salesiq || zohoObject;var d=document;s=d.createElement("script");s.type="text/javascript";s.id="zsiqscript";s.defer=true;s.src="https://salesiq.zoho.com/widget";t=d.getElementsByTagName("script")[0];t.parentNode.insertBefore(s,t);</script>
        </PageContent>
    </Box>
    );
}

export default MobileRegistrationForm;